import { useContext, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  DataTable,
  DataTableSkeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  Tile,
  Modal,
} from "carbon-components-react";
import { Add16, Edit20, EditOff20 } from "@carbon/icons-react";
import dayjs from "dayjs";
import useSWR from "swr";

import { DateTimeFormat } from "../../utils/datetime";
import { UserContext } from "../../context";
import { URL, Fetcher as F } from "../../api";
import { usePuerperaAndCenter } from "../../hooks";
import { FoodVendorRow } from "../../components/Viewer";

const headers = [
  {
    key: "start_date",
    name: "開始日期",
  },
  {
    key: "employee_name",
    name: "操作人員",
  },
  {
    key: "modified",
    name: "變更日期",
  },
  {
    key: "actions",
    name: "編輯/取消",
  },
];

export const BeverageList = () => {
  const { hid } = useParams();

  const {
    token: { access },
  } = useContext(UserContext);

  const currentFVURL = `${URL.currentFoodVendorOnHistory}${hid}/`;
  const { data: currentFoodVendorData } = useSWR(
    access && [currentFVURL],
    F.withToken
  );

  const [puerpera, puerperaDisplay, center] = usePuerperaAndCenter({
    housing: hid,
  });

  const [currentFoodVendorName, setCurrentFoodVendorName] = useState();

  useEffect(() => {
    if (!currentFoodVendorData || currentFoodVendorData === null) {
      return;
    }
    setCurrentFoodVendorName(currentFoodVendorData.foodvendor_name);
  }, [currentFoodVendorData]);

  const beverageHistoryURL = `${URL.beverageHistory}?status=1&expand=employee&housing=${hid}`;
  const { data: beverageHistory = [] } = useSWR(
    access && [beverageHistoryURL],
    F.withToken
  );

  const isLoading = !beverageHistory && !puerpera;

  return (
    <Content>
      <Breadcrumb>
        <BreadcrumbItem href="/housing/">住房管理</BreadcrumbItem>
        <BreadcrumbItem href={`/housing/${hid}`}>
          {puerperaDisplay}
        </BreadcrumbItem>
        <BreadcrumbItem href="" isCurrentPage>
          更換飲品設定
        </BreadcrumbItem>
      </Breadcrumb>

      <Tile>更換飲品</Tile>

      <FoodVendorRow
        puerpera={puerpera}
        foodVendorName={currentFoodVendorName}
        center={center}
      />

      {isLoading ? (
        <DataTableSkeleton showHeader={false} />
      ) : (
        <BeverageListTable housing={hid} items={beverageHistory} />
      )}
    </Content>
  );
};

function BeverageListTable({ housing, items }) {
  const cleanData = items.map(
    ({ employee, foodvendor, start_meal, ...other }) => {
      return {
        ...other,
        foodvendor: foodvendor?.name ?? "",
        employee_name: employee?.name ?? "-",
      };
    }
  );

  const [openModal, setOpenModal] = useState(false);
  const [currentHistoryID, setCurrentHistoryID] = useState(0);
  const [modalStatus, setModalStatus] = useState("inactive");

  const handleSubmit = async () => {
    if (currentHistoryID === 0) {
      return;
    }
    setModalStatus("active");
    await F.patch(`${URL.beverageHistory}${currentHistoryID}/`, {
      status: 0,
    });
    setOpenModal(false);
    setModalStatus("finished");
  };

  return (
    <>
      <Modal
        open={openModal}
        danger
        modalHeading="您確定要取消嗎？"
        modalLabel="取消更換飲品紀錄"
        primaryButtonText="確認"
        secondaryButtonText="取消"
        loadingStatus={modalStatus}
        onRequestClose={() => setOpenModal(false)}
        onSecondarySubmit={() => setOpenModal(false)}
        onRequestSubmit={handleSubmit}
      />
      <DataTable rows={cleanData} headers={headers}>
        {({
          rows,
          headers,
          getHeaderProps,
          getRowProps,
          getTableProps,
          getToolbarProps,
          getTableContainerProps,
        }) => (
          <TableContainer {...getTableContainerProps()}>
            <TableToolbar
              {...getToolbarProps()}
              aria-label="data table toolbar"
            >
              <TableToolbarContent>
                <Button
                  renderIcon={Add16}
                  as={Link}
                  to={`/housing/${housing}/createBeverage`}
                >
                  變更飲料偏好
                </Button>
              </TableToolbarContent>
            </TableToolbar>

            <Table {...getTableProps()} isSortable>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableHeader
                      key={header.key}
                      {...getHeaderProps({ header })}
                      isSortable={header.key !== "actions"}
                    >
                      {header.name}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} {...getRowProps({ row })}>
                    {row.cells.map((cell) => {
                      const { header = "" } = cell && cell.info;
                      const id = cell.id.split(":")[0];
                      let content;
                      switch (header) {
                        case "start_date":
                        case "created":
                        case "modified":
                          if (cell.value !== null) {
                            content = dayjs(cell.value).format(
                              DateTimeFormat.date
                            );
                          } else {
                            content = cell.value;
                          }
                          break;
                        case "employee_name":
                        case "foodvendor":
                          content = cell.value;
                          break;
                        case "actions":
                          content = (
                            <div>
                              <Button
                                kind="ghost"
                                renderIcon={Edit20}
                                iconDescription="編輯"
                                as={Link}
                                to={`/housing/${housing}/beverage/${id}/edit`}
                              />
                              <Button
                                kind="ghost"
                                renderIcon={EditOff20}
                                iconDescription="取消"
                                onFocus={() => setCurrentHistoryID(id)}
                                onClick={() => setOpenModal(true)}
                              />
                            </div>
                          );
                          break;
                        default:
                          content = <span>?</span>;
                      }
                      return <TableCell key={cell.id}>{content}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>
    </>
  );
}
